import React from "react";
import { motion } from "framer-motion";

const BioAgeGraph: React.FC = () => {
    const chronologicalAge = 35;
    const bioAge = 29;
    const ageDifference = chronologicalAge - bioAge;

    const vo2Max = 48;
    const bodyFat = 18.5;
    const restingHR = 58;
    const hrv = 65;

    const getAgeStatus = (diff: number) => {
        if (diff > 5) return { text: "عالی", color: "#32D74B", emoji: "🌟", message: "بدن شما جوان‌تر از سن واقعی‌تان است!" };
        if (diff > 0) return { text: "خوب", color: "#66D4CF", emoji: "✨", message: "شما در مسیر درستی هستید." };
        if (diff > -5) return { text: "متوسط", color: "#FFD60A", emoji: "⚡", message: "فرصت بهبود وجود دارد." };
        return { text: "نیاز به بهبود", color: "#FF3B30", emoji: "🎯", message: "تغییراتی در سبک زندگی توصیه می‌شود." };
    };

    const status = getAgeStatus(ageDifference);

    return (
        <div className="space-y-3" dir="rtl">
            <div>
                <h4 className="font-medium text-[#002B5C] mb-3">سن بیولوژیکی شما</h4>

                {/* Main Display */}
                <div className="bg-gradient-to-br from-[#ECEFF1]/80 to-[#ECEFF1]/30 p-6 rounded-[20px] mb-4 shadow-[0_4px_16px_rgba(0,0,0,0.04)] relative overflow-hidden">
                    <div className="absolute top-0 right-0 text-6xl opacity-10">{status.emoji}</div>

                    <div className="flex items-center justify-between mb-4">
                        <div className="text-center flex-1">
                            <div className="text-xs text-[#9EA3A9] mb-1">سن واقعی</div>
                            <div className="text-3xl font-bold text-[#4A5568]">{chronologicalAge}</div>
                            <div className="text-xs text-[#9EA3A9]">سال</div>
                        </div>

                        <div className="flex items-center justify-center px-4">
                            <motion.svg className="w-8 h-8" fill="none" stroke={status.color} viewBox="0 0 24 24" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.5 }}>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </motion.svg>
                        </div>

                        <div className="text-center flex-1">
                            <div className="text-xs text-[#9EA3A9] mb-1">سن بیولوژیکی</div>
                            <motion.div className="text-3xl font-bold" style={{ color: status.color }} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.5, delay: 0.2 }}>
                                {bioAge}
                            </motion.div>
                            <div className="text-xs text-[#9EA3A9]">سال</div>
                        </div>
                    </div>

                    <div className="text-center p-3 bg-white/80 rounded-[12px] backdrop-blur-sm">
                        <div className="text-lg font-bold" style={{ color: status.color }}>
                            {ageDifference > 0 ? `${ageDifference} سال جوان‌تر` : `${Math.abs(ageDifference)} سال مسن‌تر`}
                        </div>
                        <div className="text-xs text-[#4A5568] mt-1">{status.message}</div>
                    </div>
                </div>

                {/* Contributing Factors */}
                <h4 className="font-medium text-[#002B5C] mb-2">شاخص‌های تاثیرگذار</h4>

                <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="bg-[#ECEFF1] p-3 rounded-[16px] text-center shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                        <div className="text-lg font-medium text-[#32D74B]">{vo2Max}</div>
                        <div className="text-xs text-[#9EA3A9]">VO₂ Max</div>
                        <div className="text-[10px] text-[#32D74B] mt-1">عالی</div>
                    </div>
                    <div className="bg-[#ECEFF1] p-3 rounded-[16px] text-center shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                        <div className="text-lg font-medium text-[#32D74B]">{bodyFat}%</div>
                        <div className="text-xs text-[#9EA3A9]">چربی بدن</div>
                        <div className="text-[10px] text-[#32D74B] mt-1">ایده‌آل</div>
                    </div>
                    <div className="bg-[#ECEFF1] p-3 rounded-[16px] text-center shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                        <div className="text-lg font-medium text-[#32D74B]">{restingHR}</div>
                        <div className="text-xs text-[#9EA3A9]">ضربان استراحت</div>
                        <div className="text-[10px] text-[#32D74B] mt-1">بسیار خوب</div>
                    </div>
                    <div className="bg-[#ECEFF1] p-3 rounded-[16px] text-center shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                        <div className="text-lg font-medium text-[#32D74B]">{hrv} ms</div>
                        <div className="text-xs text-[#9EA3A9]">HRV</div>
                        <div className="text-[10px] text-[#32D74B] mt-1">عالی</div>
                    </div>
                </div>

                {/* Age Comparison Chart */}
                <h4 className="font-medium text-[#002B5C] mb-2">مقایسه سنی</h4>
                <div className="bg-[#ECEFF1] p-4 rounded-[16px] shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                    <div className="space-y-3">
                        <div>
                            <div className="flex justify-between text-xs mb-1">
                                <span className="text-[#9EA3A9]">قدرت قلب و ریه</span>
                                <span className="font-medium text-[#32D74B]">سن ۲۵-۳۰</span>
                            </div>
                            <div className="w-full bg-white h-2 rounded-full">
                                <motion.div className="h-2 rounded-full bg-[#32D74B]" initial={{ width: 0 }} animate={{ width: "85%" }} transition={{ duration: 1 }} />
                            </div>
                        </div>

                        <div>
                            <div className="flex justify-between text-xs mb-1">
                                <span className="text-[#9EA3A9]">انعطاف‌پذیری عضلات</span>
                                <span className="font-medium text-[#66D4CF]">سن ۳۰-۳۵</span>
                            </div>
                            <div className="w-full bg-white h-2 rounded-full">
                                <motion.div className="h-2 rounded-full bg-[#66D4CF]" initial={{ width: 0 }} animate={{ width: "70%" }} transition={{ duration: 1, delay: 0.2 }} />
                            </div>
                        </div>

                        <div>
                            <div className="flex justify-between text-xs mb-1">
                                <span className="text-[#9EA3A9]">سلامت متابولیسم</span>
                                <span className="font-medium text-[#32D74B]">سن ۲۵-۳۰</span>
                            </div>
                            <div className="w-full bg-white h-2 rounded-full">
                                <motion.div className="h-2 rounded-full bg-[#32D74B]" initial={{ width: 0 }} animate={{ width: "88%" }} transition={{ duration: 1, delay: 0.4 }} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Improvement Tips */}
                <div className="mt-3 p-3 bg-gradient-to-r from-[#0072CE]/10 to-[#00E5FF]/10 rounded-[16px] border border-[#0072CE]/20">
                    <h5 className="text-sm font-medium text-[#0072CE] mb-2 flex items-center gap-2">
                        <span>💡</span>
                        راهکارهای بهبود سن بیولوژیکی
                    </h5>
                    <ul className="text-xs text-[#4A5568] space-y-1">
                        <li className="flex items-start gap-2">
                            <span className="text-[#0072CE] mt-0.5">•</span>
                            <span>تمرینات قلبی-عروقی منظم (۳ بار در هفته)</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-[#0072CE] mt-0.5">•</span>
                            <span>تمرینات مقاومتی برای حفظ توده عضلانی</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-[#0072CE] mt-0.5">•</span>
                            <span>خواب کافی (۷-۹ ساعت در شب)</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-[#0072CE] mt-0.5">•</span>
                            <span>مدیریت استرس با مدیتیشن و یوگا</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default BioAgeGraph;
